import { BASE_API_URL } from "../../../constants";
import fpFetch from "../../../services/fpFetch";
import store from "../../../redux/store";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";

/** Quiet time after the last edit before the content is written. */
export const SAVE_DEBOUNCE_MS = 500;
/** Longest `flushPendingSaves` waits for a write already on the wire. */
export const FLUSH_WAIT_MS = 5000;

// Page-scoped by design: the report editor autosaves through this module from
// several components (the editor, the query cells, the header's reads), so the
// last saved content and the one pending write are shared by the page rather
// than owned by a component. `cancelPendingSaves` below is the reset
// (rules/frontend.md, Module state and effects, rule 5).
const editorContent = {
  lastSavedContent: "",
};

export const resetLastSavedEditorContent = () => {
  editorContent.lastSavedContent = "";
};

export const setLastSavedEditorContent = (content) => {
  editorContent.lastSavedContent = content;
};

const contentIsChanged = (content) => {
  return content !== editorContent.lastSavedContent;
};

const updateBackend = async ({ content, workspaceId }) => {
  try {
    window.savingWorkspaceContent = true;
    // A failure belongs to one write: cleared as the next one starts, set in
    // the catch below, and read by the header's save indicator alongside the
    // in-flight flag (which the finally clears whether the write landed or
    // not, so it cannot carry the failure itself).
    window.workspaceContentSaveFailed = false;
    window.dispatchEvent(new Event("savingContentStateChange"));

    const { type } = store.getState().fpAppV4Data;
    const update = async (content) => {
      const response = await fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: workspaceId,
          content: content,
          type: type || "private_doc_editor",
        }),
      });
      if (!response?.ok && response?.status !== 401) {
        throw new Error(`Request failed with status ${response?.status}`);
      }
      return response;
    };
    if (contentIsChanged(content)) {
      await update(content);
      return;
    }
  } catch (error) {
    console.log("error: ", error);
    window.workspaceContentSaveFailed = true;
  } finally {
    window.savingWorkspaceContent = false;
    window.dispatchEvent(new Event("savingContentStateChange"));
  }
};

// The one debounced write: the last edit within SAVE_DEBOUNCE_MS wins. On the
// product timer module, so a story's clock drives it (#12187); a lodash
// debounce read the real clock when armed and the fake one when re-armed.
let pendingSave = null;

const runPendingSave = () => {
  if (!pendingSave) return;
  const { args, timer } = pendingSave;
  pendingSave = null;
  clearProductTimeout(timer);
  updateBackend(args);
};

const scheduleSave = (args) => {
  clearProductTimeout(pendingSave?.timer);
  pendingSave = {
    args,
    timer: setProductTimeout(runPendingSave, SAVE_DEBOUNCE_MS),
  };
};

/**
 * Drop the pending write and forget the page's save state: the last saved
 * content and both broadcast flags. For a page that starts over (a story, a
 * sign-out); a write already on the wire still settles on its own.
 */
export const cancelPendingSaves = () => {
  clearProductTimeout(pendingSave?.timer);
  pendingSave = null;
  editorContent.lastSavedContent = "";
  window.savingWorkspaceContent = false;
  window.workspaceContentSaveFailed = false;
};

export const flushPendingSaves = async () => {
  runPendingSave();
  if (window.savingWorkspaceContent) {
    await new Promise((resolve) => {
      let timer = null;
      const done = () => {
        window.removeEventListener("savingContentStateChange", handler);
        clearProductTimeout(timer);
        resolve();
      };
      const handler = () => {
        if (!window.savingWorkspaceContent) done();
      };
      window.addEventListener("savingContentStateChange", handler);
      timer = setProductTimeout(done, FLUSH_WAIT_MS);
    });
  }
};

export const saveWorkspaceContent = ({ content, workspaceId }) => {
  if (!contentIsChanged(content)) return;
  window.savingWorkspaceContent = true;
  window.dispatchEvent(new Event("savingContentStateChange"));
  scheduleSave({ content, workspaceId });
};
